import { useEffect, useRef } from 'react';

/**
 * Logs which values changed between renders to help debug unnecessary re-renders.
 * Only active in development mode.
 *
 * @param name - Component or hook name to appear in the log
 * @param values - Object of tracked values (props, state, context)
 */
export function useWhyDidYouUpdate(
  name: string,
  values: Record<string, unknown>,
): void {
  const previousValues = useRef<Record<string, unknown>>({});

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;

    const allKeys = Object.keys({ ...previousValues.current, ...values });
    const changedEntries: Record<string, { from: unknown; to: unknown }> = {};

    for (const key of allKeys) {
      if (previousValues.current[key] !== values[key]) {
        changedEntries[key] = {
          from: previousValues.current[key],
          to: values[key],
        };
      }
    }

    if (Object.keys(changedEntries).length > 0) {
      // eslint-disable-next-line no-console
      console.log(
        '[WHY RENDER]',
        JSON.stringify(
          { component: name, changes: changedEntries },
          (_key, val) => {
            if (val instanceof Node) return `[${val.constructor.name}]`;
            if (typeof val === 'function') return '[Function]';
            if (val instanceof Set) return [...val];
            if (val instanceof Map) return Object.fromEntries(val);
            return val;
          },
          2,
        ),
      );
    }

    previousValues.current = values;
  });
}
